import {
  ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  FISCAL_SCENARIO_MODEL_VERSION,
  type FiscalScenarioInput,
  type FiscalScenarioResult,
} from '../../types/economicContribution';
import { finiteNonNegative, finitePositive, hashEconomicModelInput, roundEconomic } from './modelUtils';

function percentAt(values: readonly number[] | undefined, index: number): number {
  const value = values?.[index] ?? 0;
  return Math.min(100, Math.max(0, value));
}

function dollarsAt(values: readonly number[] | undefined, index: number): number {
  return Math.max(0, values?.[index] ?? 0);
}

/** Manual arithmetic only. No taxability, assessment, exemption, or incentive eligibility is inferred. */
export function calculateFiscalScenario(input: FiscalScenarioInput): FiscalScenarioResult {
  if (!input.scenarioId.trim() || !input.jurisdictionLabel.trim()) throw new RangeError('Scenario id and jurisdiction are required.');
  if (!Number.isInteger(input.startYear) || input.startYear < 1900 || input.startYear > 2500) throw new RangeError('startYear is invalid.');
  if (!Number.isInteger(input.currencyYear) || input.currencyYear < 1900 || input.currencyYear > 2500) throw new RangeError('currencyYear is invalid.');
  if (!Number.isInteger(input.years) || input.years < 1 || input.years > 100) throw new RangeError('years must be an integer from 1 to 100.');
  if (!finiteNonNegative(input.assessedValueUsd)) throw new RangeError('assessedValueUsd must be finite and non-negative.');
  if (!finitePositive(input.annualTaxRateDecimal) || input.annualTaxRateDecimal > 1) {
    throw new RangeError('annualTaxRateDecimal must be greater than 0 and no more than 1.');
  }
  for (const value of input.abatementPctByYear ?? []) {
    if (!finiteNonNegative(value) || value > 100) throw new RangeError('Abatement percentages must be between 0 and 100.');
  }
  for (const value of input.pilotUsdByYear ?? []) {
    if (!finiteNonNegative(value)) throw new RangeError('PILOT values must be finite and non-negative.');
  }

  const treatment = input.pilotTreatment ?? 'replacement';
  let cumulative = 0;
  const years = Array.from({ length: input.years }, (_, index) => {
    const grossTaxUsd = roundEconomic(input.assessedValueUsd * input.annualTaxRateDecimal, 2);
    const abatementPct = percentAt(input.abatementPctByYear, index);
    const taxAfterAbatementUsd = roundEconomic(grossTaxUsd * (1 - abatementPct / 100), 2);
    const pilotUsd = roundEconomic(dollarsAt(input.pilotUsdByYear, index), 2);
    const communityRevenueUsd = roundEconomic(
      treatment === 'replacement' && input.pilotUsdByYear?.[index] != null
        ? pilotUsd
        : taxAfterAbatementUsd + pilotUsd,
      2,
    );
    cumulative = roundEconomic(cumulative + communityRevenueUsd, 2);
    return {
      year: input.startYear + index,
      grossTaxUsd,
      abatementPct,
      taxAfterAbatementUsd,
      pilotUsd,
      communityRevenueUsd,
      cumulativeCommunityRevenueUsd: cumulative,
    };
  });

  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: FISCAL_SCENARIO_MODEL_VERSION,
    status: 'illustrative-screening-scenario',
    scenarioId: input.scenarioId,
    jurisdictionLabel: input.jurisdictionLabel,
    currency: 'USD',
    currencyYear: input.currencyYear,
    years,
    cumulativeCommunityRevenueUsd: cumulative,
    inputHash: hashEconomicModelInput(input),
    limitations: [
      'Illustrative screening scenario based entirely on user-entered assessment, rate, abatement, and PILOT assumptions.',
      'The platform does not determine taxability, assessed value, eligibility, statutory treatment, or negotiated PILOT terms.',
      'Values are nominal in the stated currency year; no inflation or discounting is applied.',
    ],
  };
}
